import { useState } from "react";
import {
  createRequest,
  extractFields,
  type ExtractedFields,
  type ExtractionResponse,
} from "../api";

// Sample intake email. Every name, company, project code, and dollar figure
// below is fabricated for the demo. No real person, supplier, or purchase is
// represented here. Same convention as the seed data in
// supabase/migrations/0003_seed_suppliers.sql.
const SAMPLE_EMAIL = `Subject: RFA - Replacement vacuum pumps for Utility Building 3

Hi team,

Following our sourcing review last week, we'd like to move forward with Keystone Lab Instruments for the three replacement vacuum pumps on CAP-ECHO-019. The total award is $47,800.

This goes against budget reference BUD-7104-FY26 (cost center CC-7104). Please process as a single source justification (SSJ-2026-0112) — Keystone is the only supplier whose pumps meet our vibration spec and match the existing manifold interface. Sourcing method: single_source, reason: proprietary.

PR creator: Dana Whitfield (dwhitfield@example.com)
Invoice approver: Marcus Lindqvist
Urgency: Normal, target delivery early June.

Thanks,
Dana`;

type FormState = ExtractedFields & {
  description: string;
};

const EMPTY_FORM: FormState = {
  description: "",
  request_type: "goods",
  urgency: "normal",
};

export default function IntakeForm() {
  const [text, setText] = useState(SAMPLE_EMAIL);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [extraction, setExtraction] = useState<ExtractionResponse | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Extraction runs on click, never on mount. This endpoint bills a paid
  // Anthropic key, so a page load must not spend money.
  async function handleExtract() {
    setExtracting(true);
    setError(null);
    setSubmitted(null);
    try {
      const res = await extractFields(text, "email");
      setExtraction(res);
      setForm({
        ...EMPTY_FORM,
        ...res.extracted,
        description: res.extracted.description ?? "",
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Extraction failed");
    } finally {
      setExtracting(false);
    }
  }

  async function handleSubmit() {
    if (!form.description.trim()) {
      setError("Description is required");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const created = await createRequest({
        description: form.description,
        capital_project_code: form.capital_project_code ?? null,
        budget_reference: form.budget_reference ?? null,
        cost_center: form.cost_center ?? null,
        request_type: form.request_type ?? "goods",
        value_usd: form.value ?? null,
        urgency: form.urgency ?? "normal",
        supplier_name_snapshot: form.supplier_name ?? null,
        submitted_by: form.submitted_by ?? null,
        sourcing_method: form.sourcing_method ?? null,
        single_source_reason: form.single_source_reason ?? null,
      } as any);
      setSubmitted(created.request_id);
      setForm(EMPTY_FORM);
      setExtraction(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Submit failed");
    } finally {
      setSubmitting(false);
    }
  }

  function updateField<K extends keyof FormState>(
    key: K,
    value: FormState[K]
  ) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Left: source text + extract button */}
      <div className="card p-6">
        <h2 className="text-xl font-semibold text-slate-900">
          Source document
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Paste an RFA email, memo, or approval doc. Claude Sonnet extracts the
          structured procurement fields for review.
        </p>

        <div className="mt-4">
          <label htmlFor="intake-text" className="label">
            Unstructured input
          </label>
          <textarea
            id="intake-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={16}
            className="input font-mono text-xs leading-relaxed"
          />
        </div>

        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={handleExtract}
            className="btn-primary"
            disabled={extracting || !text.trim()}
          >
            {extracting ? "Extracting…" : "Extract fields with Claude"}
          </button>
          {extraction && (
            <span className="text-xs text-slate-500">
              Confidence: {Math.round(extraction.confidence * 100)}% (
              {Object.values(extraction.extracted).filter(
                (v) => v !== null && v !== ""
              ).length}{" "}
              fields extracted)
            </span>
          )}
        </div>
      </div>

      {/* Right: extracted form */}
      <div className="card p-6">
        <h2 className="text-xl font-semibold text-slate-900">
          Review & submit
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Review the extracted fields, edit anything that looks wrong, and
          submit. The ticket is written to Supabase with a generated ID.
        </p>

        <div className="mt-4 space-y-3">
          <div>
            <label htmlFor="f-desc" className="label">
              Description
            </label>
            <textarea
              id="f-desc"
              value={form.description}
              onChange={(e) => updateField("description", e.target.value)}
              rows={2}
              className="input text-sm"
              placeholder="What is being procured?"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="f-cap" className="label">
                Capital project code
              </label>
              <input
                id="f-cap"
                value={form.capital_project_code ?? ""}
                onChange={(e) =>
                  updateField("capital_project_code", e.target.value)
                }
                className="input"
                placeholder="CAP-ECHO-019"
              />
            </div>
            <div>
              <label htmlFor="f-bud" className="label">
                Budget reference
              </label>
              <input
                id="f-bud"
                value={form.budget_reference ?? ""}
                onChange={(e) => updateField("budget_reference", e.target.value)}
                className="input"
                placeholder="BUD-7104-FY26"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="f-cc" className="label">
                Cost center
              </label>
              <input
                id="f-cc"
                value={form.cost_center ?? ""}
                onChange={(e) => updateField("cost_center", e.target.value)}
                className="input"
                placeholder="CC-7104"
              />
            </div>
            <div>
              <label htmlFor="f-val" className="label">
                Value (USD)
              </label>
              <input
                id="f-val"
                type="number"
                value={form.value ?? ""}
                onChange={(e) =>
                  updateField(
                    "value",
                    e.target.value === "" ? null : Number(e.target.value)
                  )
                }
                className="input"
                placeholder="47800"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="f-sup" className="label">
                Supplier name
              </label>
              <input
                id="f-sup"
                value={form.supplier_name ?? ""}
                onChange={(e) => updateField("supplier_name", e.target.value)}
                className="input"
                placeholder="Keystone Lab Instruments"
              />
            </div>
            <div>
              <label htmlFor="f-type" className="label">
                Type
              </label>
              <select
                id="f-type"
                value={form.request_type ?? "goods"}
                onChange={(e) => updateField("request_type", e.target.value)}
                className="input"
              >
                <option value="goods">Goods</option>
                <option value="services">Services</option>
                <option value="contract_labor">Contract labor</option>
                <option value="software">Software</option>
                <option value="equipment">Equipment</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="f-urg" className="label">
                Urgency
              </label>
              <select
                id="f-urg"
                value={form.urgency ?? "normal"}
                onChange={(e) => updateField("urgency", e.target.value)}
                className="input"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label htmlFor="f-src" className="label">
                Sourcing method
              </label>
              <select
                id="f-src"
                value={form.sourcing_method ?? ""}
                onChange={(e) => updateField("sourcing_method", e.target.value)}
                className="input"
              >
                <option value="">—</option>
                <option value="competitive_bid">Competitive bid</option>
                <option value="single_source">Single source</option>
                <option value="emergency">Emergency</option>
                <option value="framework_agreement">Framework agreement</option>
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="f-by" className="label">
              Submitted by
            </label>
            <input
              id="f-by"
              value={form.submitted_by ?? ""}
              onChange={(e) => updateField("submitted_by", e.target.value)}
              className="input"
              placeholder="Full name of requester"
            />
          </div>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !form.description.trim()}
            className="btn-primary"
          >
            {submitting ? "Creating…" : "Create request"}
          </button>
          <button
            type="button"
            onClick={() => {
              setForm(EMPTY_FORM);
              setExtraction(null);
              setSubmitted(null);
              setError(null);
            }}
            className="btn-secondary"
          >
            Clear
          </button>
        </div>

        {submitted && (
          <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
            Created request <strong>{submitted}</strong>. Open the Tracker tab
            to see it.
          </div>
        )}
        {error && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
